import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';

interface ContextInterface {
  isCartOpen: boolean;
  cartNotification: boolean;
  triggerNotification: () => void;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
}

const CartContext = createContext<ContextInterface | null>(null);

/**
 * A client component that defines the behavior that occurs when a user is interacting with a cart (for example, opening or closing it)
 */
export default function CartUIProvider({children}: {children: ReactNode}) {
  const [open, setOpen] = useState(false);
  const [cartNotification, setCartNotification] = useState<boolean>(false);

  useEffect(() => {
    //set cart notification to false after 3 seconds
    if (cartNotification) {
      setTimeout(() => {
        setCartNotification(false);
      }, 3000);
    }
  }, [cartNotification]);

  const triggerNotification = useCallback(() => {
    setCartNotification(true);
  }, []);

  const openCart = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const closeCart = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const toggleCart = useCallback(() => {
    setOpen(!open);
  }, [setOpen, open]);

  const contextValue = useMemo(() => {
    return {
      isCartOpen: open,
      triggerNotification,
      cartNotification,
      openCart,
      closeCart,
      toggleCart,
    };
  }, [
    open,
    openCart,
    closeCart,
    toggleCart,
    triggerNotification,
    cartNotification,
  ]);

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
}

export function useCartUI() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('No cart context found');
  }

  return context;
}
